import type { Invoice } from './types';

/**
 * Skonto und Verzugszinsen -- die beiden Rechnungen rund um eine offene Forderung.
 *
 * Beide sind reine Funktionen ohne Zugriff auf irgendetwas: sie bekommen den Beleg und die
 * Konditionen und liefern einen Betrag. Damit rechnet die Erfassungsmaske dasselbe wie der
 * Mahnlauf, und ein Test kann jeden Fall durchspielen, ohne eine Datenbank anzufassen.
 */

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Tage zwischen zwei ISO-Daten (b - a), auf ganze Tage. */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`);
  const to = Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

/** ISO-Datum um Tage verschieben. */
export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Offene Forderung
// ---------------------------------------------------------------------------

/**
 * Was noch aussteht: Bruttobetrag minus AUSGEGLICHEN (Geld + Skonto + Ausbuchungen).
 * Bewusst nicht minus `paidAmount` -- sonst bliebe eine mit Skonto beglichene Rechnung ewig offen.
 */
export function openAmount(invoice: Invoice): number {
  return round2(Math.max(0, invoice.grossTotal - invoice.settledAmount));
}

/** Wurde mehr gezahlt als offen war? Der Betrag, der zurückgeht oder zu verrechnen ist. */
export function overpaidAmount(invoice: Invoice): number {
  return round2(Math.max(0, invoice.settledAmount - invoice.grossTotal));
}

// ---------------------------------------------------------------------------
// Skonto
// ---------------------------------------------------------------------------

export interface CashDiscountTerms {
  /** Skontosatz in Prozent (0 = kein Skonto vereinbart). */
  readonly percent: number;
  /** Frist in Tagen ab Rechnungsdatum. */
  readonly days: number;
}

/** Der letzte Tag, an dem Skonto gezogen werden darf -- oder null, wenn keins vereinbart ist. */
export function cashDiscountDeadline(invoice: Invoice, terms: CashDiscountTerms): string | null {
  if (!(terms.percent > 0) || !(terms.days > 0)) return null;
  const start = invoice.issueDate ?? invoice.createdAt.slice(0, 10);
  if (!start) return null;
  return addDays(start, terms.days);
}

/**
 * Der zulässige Skontobetrag zu einem Stichtag. Ausserhalb der Frist: 0.
 *
 * Gerechnet auf den BRUTTObetrag -- so ist es in den Zahlungsbedingungen von FTW formuliert
 * („Bei Zahlung innerhalb von X Tagen gewähren wir Y % Skonto") und so zieht der Kunde ihn auch.
 */
export function allowedCashDiscount(
  invoice: Invoice,
  terms: CashDiscountTerms,
  onDateIso: string,
): number {
  const deadline = cashDiscountDeadline(invoice, terms);
  if (!deadline) return 0;
  if (daysBetween(onDateIso, deadline) < 0) return 0;
  return round2(invoice.grossTotal * (terms.percent / 100));
}

/**
 * Darf der offene Rest als Skonto ausgebucht werden?
 *
 * Zwei Bedingungen, beide müssen halten: die Frist ist gewahrt, UND der Rest übersteigt den
 * zulässigen Abzug nicht. Sonst ist es keine Skontozahlung, sondern eine Teilzahlung -- und die
 * darf nicht als „bezahlt" verschwinden.
 */
export function maySettleAsCashDiscount(
  invoice: Invoice,
  terms: CashDiscountTerms,
  onDateIso: string,
): boolean {
  const rest = openAmount(invoice);
  if (rest <= 0) return false;
  const erlaubt = allowedCashDiscount(invoice, terms, onDateIso);
  // Ein Cent Toleranz: der Kunde rundet seine Überweisung, wir wollen daran nicht scheitern.
  return erlaubt > 0 && rest <= erlaubt + 0.01;
}

// ---------------------------------------------------------------------------
// Verzugszinsen (§ 288 BGB)
// ---------------------------------------------------------------------------

/** Zuschlag auf den Basiszinssatz: 9 Punkte gegenüber Unternehmen, 5 gegenüber Verbrauchern. */
export const VERZUGSZINS_ZUSCHLAG_B2B = 9;
export const VERZUGSZINS_ZUSCHLAG_B2C = 5;

/**
 * Der anzuwendende Verzugszinssatz in Prozent.
 *
 * § 288 BGB: Basiszinssatz plus 9 Prozentpunkte bei Rechtsgeschäften ohne Verbraucherbeteiligung,
 * sonst plus 5. Der Basiszinssatz ändert sich zum 1. Januar und 1. Juli -- er wird deshalb im
 * Firmenprofil gepflegt und NICHT hier hartcodiert.
 */
export function verzugszinssatz(baseRatePercent: number, customerIsCompany: boolean): number {
  const zuschlag = customerIsCompany ? VERZUGSZINS_ZUSCHLAG_B2B : VERZUGSZINS_ZUSCHLAG_B2C;
  return round2(baseRatePercent + zuschlag);
}

/**
 * Verzugszinsen auf einen offenen Betrag, taggenau (actual/365).
 *
 * VORSCHLAG, kein Automatismus: Ob überhaupt Verzug eingetreten ist, hängt nach § 286 BGB auch am
 * Zugang der Rechnung und an einer etwaigen Mahnung. Der Wert ist im Mahnlauf änderbar.
 */
export function verzugszinsen(
  amount: number,
  ratePercent: number,
  fromIso: string,
  toIso: string,
): number {
  const tage = daysBetween(fromIso, toIso);
  if (tage <= 0 || amount <= 0 || ratePercent <= 0) return 0;
  return round2(amount * (ratePercent / 100) * (tage / 365));
}
